import { useState, useEffect, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import usePermissions from "@/hooks/usePermissions";
import ServicesSection from "./sections/ServicesSection";
import CreateServiceModal from "./modals/CreateServiceModal";

export default function MarketplacePage() {
  const search = useSearch();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const { hasPermission } = usePermissions();

  // Modal states
  const [showCreateServiceModal, setShowCreateServiceModal] = useState(false);

  // Filter states for services
  const [serviceFilters, setServiceFilters] = useState<Record<string, any>>({});

  // Parse filters from URL on mount
  useEffect(() => {
    const parseFiltersFromURL = () => {
      const filters: Record<string, any> = {};
      searchParams.forEach((value, key) => {
        if (key.startsWith('f_')) {
          const filterKey = key.substring(2);
          try {
            // Try to parse JSON arrays
            filters[filterKey] = JSON.parse(value);
          } catch {
            // If not JSON, treat as string or number
            filters[filterKey] = isNaN(Number(value)) ? value : Number(value);
          }
        }
      });
      return filters;
    };

    const filters = parseFiltersFromURL();
    setServiceFilters(filters);
  }, [search]);

  // Update URL when filters change
  const updateFiltersInURL = (filters: Record<string, any>) => {
    const url = new URL(window.location.href);

    // Remove existing filter params
    Array.from(url.searchParams.keys())
      .filter(key => key.startsWith('f_'))
      .forEach(key => url.searchParams.delete(key));

    // Add new filter params
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        const serialized = typeof value === 'object' ? JSON.stringify(value) : String(value);
        url.searchParams.set(`f_${key}`, serialized);
      }
    });

    window.history.replaceState({}, "", url.toString());
  };


  return (
    <div className="h-full flex flex-col">
      {/* Page Header - Only show in standalone mode */}
      <div className="px-6 py-4 border-b bg-background">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Marketplace</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage services and service options
            </p>
          </div>
          {hasPermission('marketplace:write') && (
            <Button
              onClick={() => setShowCreateServiceModal(true)}
              aria-label="Add new service"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>
          )}
        </div>
      </div>

      {/* Services Content */}
      <div className="flex-1 px-6 py-6 overflow-auto">
        <ServicesSection
          filters={serviceFilters}
          onFiltersChange={(newFilters) => {
            setServiceFilters(newFilters);
            updateFiltersInURL(newFilters);
          }}
        />
      </div>

      {/* Modals */}
      <CreateServiceModal
        open={showCreateServiceModal}
        onClose={() => setShowCreateServiceModal(false)}
      />
    </div>
  );
}